'use client'

import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { ILinkParams } from '@/setup/routing'
import { CityRoute } from '@/setup/urls/city-route'
import { useUrlParam } from '@/utils/url/use-url-param'

type IOptimisticUrlSetState<T> = (
    newValue: T | undefined,
    nextUrl: ILinkParams<'/restaurants/[citySlug]'>,
    navigateOptions?: NavigateOptions
) => void

export function useOptimisticSearchParamState<T extends number | string | string[]>(
    key: string,
    urlType: 'search' | 'path',
    type: T extends string ? 'string' : T extends number ? 'number' : 'array',
    method: 'replace' | 'push'
): [T | undefined, IOptimisticUrlSetState<T>] {
    const rawValue = useUrlParam(key, urlType)

    const value = useMemo(() => {
        if (!rawValue) return undefined

        if (type === 'array') {
            return rawValue.split(',')
        }

        if (type === 'number') {
            const rawNumber = Number(rawValue)

            if (isNaN(rawNumber)) return undefined

            return rawNumber
        }

        return rawValue
    }, [rawValue, type])

    const [state, setState] = useState<T | undefined>(value as T | undefined)

    useEffect(() => {
        setState(value as T | undefined)
    }, [value])

    const optimisticSetState = useOptimisticUrlSetState<T>(method, setState)

    return useMemo(() => [state, optimisticSetState] as const, [state, optimisticSetState])
}

/**
 * Returns a functino which accepts a function to update the URL and returns a function to update the state
 * The state is updated and flushed immediately, and the URL is updated after the state is updated
 */
function useOptimisticUrlSetState<T>(
    method: 'replace' | 'push',
    setState: (newValue: T | undefined) => void
): IOptimisticUrlSetState<T> {
    const router = useRouter()

    return useCallback(
        (newValue, nextUrl, navigateOptions) => {
            flushSync(() => {
                setState(newValue)
            })

            const url = CityRoute.createUrl(nextUrl.params)

            // const nextUri = `

            switch (method) {
                case 'replace':
                    return router.replace(url, navigateOptions)

                case 'push':
                    return router.push(url, navigateOptions)
            }
        },
        [setState, router, method]
    )
}
